import { NetworkError, ParseError } from '../../lib/errors.ts';
import { proxyFetch as defaultProxyFetch, type ProxyFetchOpts } from '../../lib/proxy.ts';
import type { SearchQuery } from '../types.ts';
import {
  defaultChapterIdFromComic,
  parseGameBusChapters,
  parseGameBusComic,
  parseGameBusPageList,
  parseGameBusPages,
  parseGameBusTags,
} from './parsers.ts';
import {
  GAMEBUS_API_BASE,
  GAMEBUS_SORT_OPTIONS,
  GAMEBUS_SOURCE_ID,
  GAMEBUS_TAG_FILTER_ID,
  type GameBusSort,
} from './types.ts';

export interface GameBusApiOptions {
  proxyFetch?: (url: string, opts?: ProxyFetchOpts) => Promise<Response>;
}

function sortValue(value: string | undefined): GameBusSort {
  return GAMEBUS_SORT_OPTIONS.some((option) => option.value === value)
    ? (value as GameBusSort)
    : 'last_chapterAt';
}

function selectedTagId(query: SearchQuery): string {
  const value = query.extra?.[GAMEBUS_TAG_FILTER_ID];
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return '';
}

async function readJson(response: Response, label: string): Promise<unknown> {
  if (!response.ok) {
    throw new NetworkError(`GameBus ${label} request failed with status ${response.status}`, {
      source: GAMEBUS_SOURCE_ID,
      status: response.status,
    });
  }
  try {
    return await response.json() as unknown;
  } catch (err) {
    throw new ParseError(`GameBus ${label} response was not JSON`, {
      source: GAMEBUS_SOURCE_ID,
      cause: err,
    });
  }
}

export function createGameBusApi(options: GameBusApiOptions = {}) {
  const proxyFetch = options.proxyFetch ?? defaultProxyFetch;

  async function requestJson(path: string, opts: ProxyFetchOpts = {}, label = path): Promise<unknown> {
    const response = await proxyFetch(`${GAMEBUS_API_BASE}${path}`, {
      ...opts,
      headers: {
        Accept: 'application/json',
        ...(opts.headers ?? {}),
      },
    });
    return readJson(response, label);
  }

  return {
    async listComics(query: SearchQuery) {
      const page = Math.max(1, query.page ?? 1);
      const body = {
        keyword: query.q?.trim() ?? '',
        page,
        sort: sortValue(query.sort),
        tid: selectedTagId(query),
      };
      const payload = await requestJson(
        `/comic/page/${page}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        'list',
      );
      return parseGameBusPageList(payload);
    },

    async getComic(id: string) {
      const [detailPayload, chapterPayload] = await Promise.all([
        requestJson(`/comic/${encodeURIComponent(id)}`, {}, 'detail'),
        requestJson(`/comic/chapters?comicId=${encodeURIComponent(id)}`, {}, 'chapters'),
      ]);
      const chapters = parseGameBusChapters(chapterPayload);
      return parseGameBusComic(detailPayload, chapters);
    },

    async getPages(id: string, readUnitId?: string) {
      let chapterId = readUnitId;
      if (!chapterId) {
        const [detailPayload, chapterPayload] = await Promise.all([
          requestJson(`/comic/${encodeURIComponent(id)}`, {}, 'detail'),
          requestJson(`/comic/chapters?comicId=${encodeURIComponent(id)}`, {}, 'chapters'),
        ]);
        chapterId = defaultChapterIdFromComic(detailPayload, parseGameBusChapters(chapterPayload));
      }
      if (!chapterId) {
        throw new ParseError('GameBus comic did not include any readable chapters', {
          source: GAMEBUS_SOURCE_ID,
        });
      }
      return parseGameBusPages(
        await requestJson(`/comic/${encodeURIComponent(chapterId)}/pages`, {}, 'pages'),
      );
    },

    async listTags() {
      return parseGameBusTags(await requestJson('/tag/comic', {}, 'tags'));
    },
  };
}
